<template>
  <div>
    <actionable-panel
      :header-text="headerText"
      :complete="complete"
      :active="active"
      :current-step="currentStep"
      :total-steps="totalSteps"
      :disable-edit="editDisabled"
      @edit="transitionEdit"
    >
      <expand-transition v-if="isWroCreated" slot="info">
        <div class="alert-message">
          <SbBanner variation="warning">
            <p class="sb-banner__text">
              Please Note: When altering product quantities or lot numbers, you will need to re-enter shipment configurations for the updated product in step 4.
              <span v-if="!isPoweredBySubAccountUser">
                Learn more
                <help-center-link link-key="editWro/changeProductsConfig">
                  here
                </help-center-link>.
              </span>
            </p>
          </SbBanner>
        </div>
      </expand-transition>
      <expand-transition slot="table">
        <div class="wro-actionable-panel__body">
          <sb-dropdown-search
            id="wro-products-search" v-model="selectedProducts"
            class="wro-product-search-bar"
            :options="options"
            :show-selected-options="true"
            :placeholder="isSearchByIdSkuNameEnabled ? $t('wro.placeholderTextAll') : $t('wro.placeholderText')"
            :loading="loading"
            :comparator="selectedProductsComparator"
            @search="handleSearch"
          >
            <sb-selected-dropdown-search-option
              slot="selection"
              slot-scope="{ selection }"
              class="wro-actionable-panel__selection-placeholder"
              :value="selection.name || isSearchByIdSkuNameEnabled ? $t('wro.placeholderTextAll') : $t('wro.placeholderText')"
            />
            <sb-dropdown-option slot-scope="{ option }">
              {{ option.name }}
            </sb-dropdown-option>
          </sb-dropdown-search>
          <a v-if="isShowUploadExcel" class="import-button" data-testid="import-button" @click.prevent="toggleModal">
            Import
          </a>
        </div>
      </expand-transition>
      <expand-transition slot="table">
        <product-table
          v-if="hasSelectedProducts"
          :rows="selectedProducts"
          :columns="currentColumnDefinitions"
          :contains-dangerous-items="selectedProductsHasDangerous"
          :mobile-display="isMobileDevice"
          :state-machine="machine"
          :warnings="warnings"
          :show-platform-column="!isSearchByIdSkuNameEnabled"
          @checkDangerousGoodsV1="checkDangerousV1"
          @checkDangerousGoodsV2="checkDangerousV2"
        />
      </expand-transition>
      <div slot="footer" class="wro-actionable-panel__footer">
        <div v-if="hasToggleProductIdsLot || hasToggleProductIdsNotLot || hasToggleProductIdsAreDangerous || hasToggleProductIdsAreNotDangerous" class="alert-message">
          <SbBanner variation="warning" class="alter-prod-warning">
            <p class="sb-banner__text">
              Note that you are updating the following product attributes:<br>
              <ul class="attribute-list">
                <li v-if="hasToggleProductIdsLot || hasToggleProductIdsNotLot">
                  Lot details
                </li>
                <li v-if="hasToggleProductIdsAreDangerous || hasToggleProductIdsAreNotDangerous">
                  Dangerous goods
                </li>
              </ul>
            </p>
          </SbBanner>
        </div>
        <div v-if="hasSelectedProducts" class="wro-actionable-panel__footer__checkbox">
          <div v-if="NewHazmatWROExperience && !isPoweredBySubAccountUser">
            <SbBanner v-if="isAnySdsNotUploaded" variation="danger" class="alter-prod-warning">
              <sb-checkbox v-model="checkForDangerousGoods">
                <p data-testid="dangerous-goods-footer-danger">
                  {{ $t('wro.footerDangerText1') }}
                  <help-center-link link-key="createWro/hazmatGuidelines">
                    {{ $t('wro.footerLinkText') }}
                  </help-center-link>
                  <br>
                  <br>
                  <span>
                    {{ $t('wro.footerDangerText2') }}
                    <help-center-link link-key="createWro/hazmatGuidelines">
                      {{ $t('wro.footerLinkText') }}
                    </help-center-link>
                  </span>
                </p>
              </sb-checkbox>
            </SbBanner>
            <SbBanner v-else variation="warning" class="alter-prod-warning">
              <sb-checkbox v-model="checkForDangerousGoods">
                <p data-testid="dangerous-goods-footer-warning">
                  {{ $t('wro.footerOriginText') }}
                  <span>
                    {{ $t('wro.footerWarningText') }}
                    <help-center-link link-key="createWro/hazmatGuidelines">
                      {{ $t('wro.footerLinkText') }}
                    </help-center-link>
                  </span>
                </p>
              </sb-checkbox>
            </SbBanner>
          </div>
          <div v-else>
            <sb-checkbox v-model="checkForDangerousGoods">
              <p data-testid="dangerous-goods-original-footer">
                {{ $t('wro.footerOriginText') }}
                <help-center-link v-if="!isPoweredBySubAccountUser" link-key="createWro/dangerousGoods">
                  {{ $t('wro.footerOriginLinkText') }}
                </help-center-link>
              </p>
            </sb-checkbox>
          </div>
        </div>
        <div v-if="hasSelectedProducts" class="wro-actionable-panel__footer__checkbox">
          <sb-checkbox v-if="hasToggleProductIdsAreDangerous" v-model="checkForDangerousGoodsUpdate">
            <p>
              I understand that my dangerous goods are subject to
              <a v-if="!isPoweredBySubAccountUser" target="_blank" :href="wroDangerousGoodsListHref"> ShipBob approval</a>
              <span v-else>approval</span>
              and I will not send inventory to a fulfilment center until approved.
            </p>
          </sb-checkbox>
        </div>
        <div>
          <sb-async-button :loading-promise="savingWro" :disabled="disableSave" class="wro-save-button wro-actionable-panel__action" @click="transitionSave">
            Save & Continue
          </sb-async-button>
          <a class="wro-actionable-panel__action" href="#" @click.prevent="transitionClear">Cancel</a>
        </div>
      </div>
      <import-products-to-wro :open-modal="isModalOpen" @closeModal="toggleModal" />
    </actionable-panel>
  </div>
</template>

<script>
// import differenceWith from 'lodash/differenceWith'
import ShipbobAppSettings from '@/appSettings/appSetting'
import stepsMixin from './stepsMixin'
import flatten from 'lodash/flatten'
import ImportProductsToWro from '@/components/createWarehouseReceivingOrder/steps/ImportProductsToWro'
import ProductTable from '@/components/createWarehouseReceivingOrder/ProductTable'

const lotAndExpirationHelpContent =
  'Identification number/batch number assigned to a particular quantity and its expiration date'
const columnDefinitions = [
  { name: 'Inventory ID', field: 'id' },
  { name: 'Item Name', field: 'name' },
  { name: 'SKU', field: 'sku' },
  { name: 'Platform', field: 'platformName' },
  { name: 'Quantity to Send', field: 'quantity' },
  { name: 'Lot and Expiration', field: 'lot' }
]

export default {
  name: 'SelectProducts',
  components: {
    'import-products-to-wro': ImportProductsToWro,
    'product-table': ProductTable
  },
  directives: {},
  filters: {},
  mixins: [stepsMixin],
  inject: ['isPoweredBySubAccountUser'],
  props: {},
  data: () => ({
    loading: false,
    columnDefinitions,
    errors: {},
    invalidLot: {},
    isCheckedDangerousGoods: false,
    isCheckedDangerousGoodsUpdate: false,
    isMobileDevice: window.innerWidth <= 640,
    lotAndExpirationHelpContent,
    wroDangerousGoodsListHref: ShipbobAppSettings.wroDangerousGoodsListHref,
    isModalOpen: false
  }),
  computed: {
    isSearchByIdSkuNameEnabled () {
      return this.$shipBobStore.getters.enableNewProductCatalog && !this.$shipBobStore.getters['Inbound.WROProductSearchExclusion']
    },
    headerText () {
      return `Search for item(s) you’re sending ${this.isPoweredBySubAccountUser ? 'us' : 'to ShipBob'}`
    },
    checkForDangerousGoods: {
      get () {
        if (
          !this.isCheckedDangerousGoods ||
          (this.hasToggleProductIdsAreDangerous &&
            !this.isCheckedDangerousGoodsUpdate)
        ) { this.machine.transition('INVALID') }
        return this.isCheckedDangerousGoods
      },
      set (val) {
        this.isCheckedDangerousGoods = val
        if (
          this.isCheckedDangerousGoods &&
          (!this.hasToggleProductIdsAreDangerous ||
            this.isCheckedDangerousGoodsUpdate) &&
          this.quantitiesAreValid() &&
          this.lotsAreValid()
        ) { this.machine.transition('VALIDATED_AND_CHECKED') } else this.machine.transition('INVALID')
      }
    },
    checkForDangerousGoodsUpdate: {
      get () {
        if (
          !this.isCheckedDangerousGoods ||
          (this.hasToggleProductIdsAreDangerous &&
            !this.isCheckedDangerousGoodsUpdate)
        ) { this.machine.transition('INVALID') }
        return this.hasToggleProductIdsAreDangerous
          ? this.isCheckedDangerousGoodsUpdate
          : true
      },
      set (val) {
        this.isCheckedDangerousGoodsUpdate = val
        if (
          this.isCheckedDangerousGoods &&
          (!this.hasToggleProductIdsAreDangerous ||
            this.isCheckedDangerousGoodsUpdate) &&
          this.quantitiesAreValid() &&
          this.lotsAreValid()
        ) { this.machine.transition('VALIDATED_AND_CHECKED') } else this.machine.transition('INVALID')
      }
    },
    hasSelectedProducts () {
      return this.selectedProducts.length > 0
    },
    selectedProductsHasDangerous () {
      return this.selectedProducts.some((x) => x.isDangerousGoods === true)
    },
    hasToggleProductIdsLot () {
      return (
        this.$shipBobStore.getters[
          this.StoreModuleName + '/toggleProductIdsLot'
        ].length > 0
      )
    },
    hasToggleProductIdsNotLot () {
      return (
        this.$shipBobStore.getters[
          this.StoreModuleName + '/toggleProductIdsNotLot'
        ].length > 0
      )
    },
    hasToggleProductIdsAreDangerous () {
      return (
        this.$shipBobStore.getters[
          this.StoreModuleName + '/toggleProductIdsAreDangerous'
        ].length > 0
      )
    },
    hasToggleProductIdsAreNotDangerous () {
      return (
        this.$shipBobStore.getters[
          this.StoreModuleName + '/toggleProductIdsAreNotDangerous'
        ].length > 0
      )
    },
    selectedProducts: {
      get () {
        return (
          this.$shipBobStore.getters[
            this.StoreModuleName + '/selectedProducts'
          ] || []
        )
      },
      set (n) {
        this.$shipBobStore.dispatch(
          this.StoreModuleName + '/setSelectedProducts',
          n
        )
      }
    },
    productQuantities () {
      return this.$shipBobStore.getters[
        this.StoreModuleName + '/productQuantities'
      ]
    },
    options () {
      return this.$shipBobStore.getters[
        this.StoreModuleName + '/filteredProducts'
      ]
    },
    isShowUploadExcel () {
      return this.$shipBobStore.getters.enableUploadExcelAtCreateWRO
    },
    warnings: {
      get () {
        return (
          this.$shipBobStore.getters[
            this.StoreModuleName + '/warnings'
          ]
        )
      }
    },
    WROProductSearchExclusion () {
      return this.$shipBobStore.getters['Inbound.WROProductSearchExclusion']
    },
    NewHazmatWROExperience () {
      return this.$shipBobStore.getters['Inbound.NewHazmatWROExperience']
    },

    isAnySdsNotUploaded () {
      return this.selectedProducts.some(x => x.sdsStatus === 1)
    },
    currentColumnDefinitions () {
      const dangerousColumn = {
        name: this.NewHazmatWROExperience ? 'Dangerous Good?' : 'Dangerous?', field: 'isDangerousGoods'
      }

      return [
        ...columnDefinitions,
        dangerousColumn,
        { name: 'Action', field: 'action' }
      ]
    }
  },
  watch: {
    selectedProducts: {
      handler (n, o) {
        if (n.length > o.length) {
          this.machine.transition('PRODUCT_ADDED')
        } else if (n.length === 0) {
          this.machine.transition('ALL_PRODUCTS_REMOVED')
        } else if (this.isCheckedDangerousGoods &&
                   this.quantitiesAreValid() &&
                   this.lotsAreValid()) {
          this.machine.transition('VALIDATED_AND_CHECKED')
        } else {
          this.machine.transition('INVALID')
        }
      }
    }
  },
  mounted () {
  },
  created () {
    window.addEventListener('resize', this.onWindowResize)
  },
  destroyed () {
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    productsMapper (products) {
      const tranformedProduct = products.map(p => ({
        id: p.inventoryId,
        name: p.productName,
        sku: p.variantSku,
        isDangerousGoods: false,
        isLotProduct: false,
        barcode: '',
        description: '',
        tariffCode: '',
        platform: {
          id: 15,
          name: 'platformSources.ExcelUpload'
        },
        quantitiesToSend: [
          {
            amount: 0,
            lotNumber: '',
            expirationDate: '',
            _id: 0
          }
        ]
      }
      ))
      return tranformedProduct
    },
    checkDangerousV1 () {
      this.checkForDangerousGoods = this.isCheckedDangerousGoods
    },
    checkDangerousV2 () {
      this.checkForDangerousGoodsUpdate = false
    },
    lotsAreValid () {
      const qts = []
      this.selectedProducts
        .filter((p) => p.isLotProduct)
        .forEach((p) => {
          p.quantitiesToSend.forEach((x) => {
            qts.push({
              id: p.id,
              amount: x.amount,
              lotNumber: x.lotNumber,
              expirationDate: x.expirationDate
            })
          })
        })
      const flattenedQts = flatten(qts)
      const someInvalid = flattenedQts
        .map((qts) => this.validateLotInfo(qts.lotNumber, qts.expirationDate))
        .some((val) => val === false)
      const lotInfoSetSize = new Set(
        flattenedQts.flatMap((x) => x.id + x.lotNumber + x.expirationDate)
      ).size
      return !someInvalid && lotInfoSetSize === flattenedQts.length
    },
    onWindowResize () {
      this.isMobileDevice = window.innerWidth <= 640
    },
    async handleSearch ({ searchTerm }) {
      this.loading = true
      const action = this.isSearchByIdSkuNameEnabled ? '/searchProductsV2' : '/searchProducts'
      await this.$shipBobStore.dispatch(
        this.StoreModuleName + action,
        searchTerm
      )
      this.loading = false
    },
    quantitiesAreValid () { // duped
      return !flatten(
        this.selectedProducts.map((product) =>
          product.quantitiesToSend.map((qts) =>
            this.validateQuantity(qts.amount)
          )
        )
      ).some((val) => val === false)
    },
    isValidDateEntered (date) { // duped
      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)
      const isDateValid = !isNaN(Date.parse(date)) // Date.parse will return NaN for an invalid date
      const isDateInPast =
        isDateValid &&
        new Date(date).toISOString() >= currentDate.toISOString()
      return isDateInPast && isDateValid
    },
    validateLotInfo (lotNumber, lotDate) { // duped
      const parsedLotDate = lotDate || ''
      const validParsedLotDate = this.isValidDateEntered(parsedLotDate)
      return (
        (lotNumber.length > 0 &&
          (!parsedLotDate.length > 0 || validParsedLotDate)) ||
        (parsedLotDate.length > 0 && validParsedLotDate)
      )
    },
    validateQuantity (quantity) { // duped
      const parsedValue =
        typeof quantity === 'string' ? parseInt(quantity) : quantity
      return !(isNaN(parsedValue) || parsedValue <= 0)
    },
    selectedProductsComparator (a, b) {
      return a.id === b.id
    },
    toggleModal () {
      this.isModalOpen = !this.isModalOpen
    }
  },
  i18n: {
    messages: {
      'en-US': {
        wro: {
          placeholderText: 'Search by item name or Inventory ID',
          placeholderTextAll: 'Search by Item Name, SKU or Inventory ID',
          footerOriginText: 'I confirm that I have marked all applicable items as dangerous goods.',
          footerWarningText: 'If items are identified as dangerous goods during receiving, there may be delays in receiving and/or my items will be quarantined. For more information, read',
          footerDangerText1: 'I confirm that I have marked all applicable items as dangerous goods. If items are identified as dangerous goods during receiving, there may be delays in receiving and/or my items will be quarantined. For more information, read ',
          footerDangerText2: 'I acknowledge some of my products that are marked as dangerous goods are currently missing a required Safety Data Sheet (SDS). If I dont upload an SDS before receiving begins, then there may be delays in receiving and/or these dangerous goods may be quarantined. For more information, read ',
          footerOriginLinkText: "Here's why it's important.",
          footerLinkText: "ShipBob's Shipping Guidelines."
        }
      }
    }
  }
}
</script>

<style scoped lang='scss'>
@import "./panel.scss";
.attribute-list {
  list-style: circle;
  margin-bottom: 0em;
}
.sb-banner {
  margin: 0 1.5rem 1.5rem 1.5rem;
  &__text {
    margin: 0;
  }
  &.alter-prod-warning {
    margin: 0 0 1.5rem 0;
  }
}
.wro-actionable-panel__footer__checkbox {
  display: flex;
}

.wro-actionable-panel__body {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  width: 100%;
  padding: 2em 5em;
}

.wro-product-search-bar {
  padding-right: 2em;
  flex-grow: 2;
}

a.import-button {
  color: #2470af;
}
</style>
